import { useState, useEffect } from 'react';
import { useQuery, useSubscription, useMutation } from '@apollo/client/react';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { LIST_MY_BULLETIN_POSTS, ON_BULLETIN_POST_CREATED, ADD_REACTION, REMOVE_REACTION } from '../graphql/bulletinBoard';
import { GET_CURRENT_USER } from '../graphql/queries';
import { CreatePostModal } from '../components/bulletin/CreatePostModal';
import { PostDetailModal } from '../components/bulletin/PostDetailModal';
import toast from 'react-hot-toast';
import { STAFF_ROLES } from '../utils/permissions';

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface ListMyBulletinPostsData {
  listMyBulletinPosts: {
    items: BulletinPost[];
  };
}

interface OnBulletinPostCreatedData {
  onBulletinPostCreated: BulletinPost;
}

interface PostImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  size?: number;
}

interface BulletinAttachment {
  id: string;
  url: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
  isImage: boolean;
}

interface PostReactions {
  thumbsUp: string[];
  heart: string[];
  party: string[];
  eyes: string[];
}

interface BulletinPost {
  id: string;
  content: string;
  audienceType: string;
  creatorId: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  targetedUserIds?: string[];
  priority: string;
  isPinned: boolean;
  images: PostImage[];
  attachments?: BulletinAttachment[];
  reactions: PostReactions;
}

export function BulletinBoard() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BulletinPost | null>(null);

  const { data: userData, loading: userLoading } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const { data: postsData, loading: postsLoading, refetch } = useQuery<ListMyBulletinPostsData>(LIST_MY_BULLETIN_POSTS, {
    variables: { limit: 50 },
  });

  const [addReaction] = useMutation(ADD_REACTION);
  const [removeReaction] = useMutation(REMOVE_REACTION);

  const user = userData?.getCurrentUser;
  const rawPosts = postsData?.listMyBulletinPosts?.items || [];
  
  // Sort posts: pinned first, then by createdAt descending
  const posts = [...rawPosts].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  
  const isStaff = user?.role && STAFF_ROLES.includes(user.role as any);

  // Subscribe to new posts
  const { data: subscriptionData } = useSubscription<OnBulletinPostCreatedData>(ON_BULLETIN_POST_CREATED, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  // Handle new post from subscription
  useEffect(() => {
    if (subscriptionData?.onBulletinPostCreated) {
      // Refetch posts to include the new post
      refetch();
    }
  }, [subscriptionData, refetch]);

  const truncateContent = (content: string, maxLength: number = 300): string => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays < 7) {
      // Relative time for posts within last 7 days
      if (diffInDays === 0) {
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        if (diffInHours === 0) {
          const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
          return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
        }
        return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
      }
      return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
    }

    // Absolute date for posts older than 7 days
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return {
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: '🚨',
          label: 'Urgent',
        };
      case 'IMPORTANT':
        return {
          color: 'bg-amber-100 text-amber-800 border-amber-200',
          icon: '⚠️',
          label: 'Important',
        };
      default:
        return null;
    }
  };

  const getReactionCount = (reactions: PostReactions, emoji: string): number => {
    const emojiMap: { [key: string]: keyof PostReactions } = {
      '👍': 'thumbsUp',
      '❤️': 'heart',
      '🎉': 'party',
      '👀': 'eyes',
    };
    const key = emojiMap[emoji];
    return reactions?.[key]?.length || 0;
  };

  const hasUserReacted = (reactions: PostReactions, emoji: string, userId: string): boolean => {
    const emojiMap: { [key: string]: keyof PostReactions } = {
      '👍': 'thumbsUp',
      '❤️': 'heart',
      '🎉': 'party',
      '👀': 'eyes',
    };
    const key = emojiMap[emoji];
    return reactions?.[key]?.includes(userId) || false;
  };

  const handleReaction = async (postId: string, emoji: string, currentlyReacted: boolean) => {
    try {
      if (currentlyReacted) {
        await removeReaction({
          variables: { postId, emoji: emoji === '👍' ? 'thumbsUp' : emoji === '❤️' ? 'heart' : emoji === '🎉' ? 'party' : 'eyes' },
        });
      } else {
        await addReaction({
          variables: { postId, emoji: emoji === '👍' ? 'thumbsUp' : emoji === '❤️' ? 'heart' : emoji === '🎉' ? 'party' : 'eyes' },
        });
      }
      refetch();
    } catch (error: any) {
      console.error('Error updating reaction:', error);
      toast.error('Failed to update reaction');
    }
  };

  if (userLoading || postsLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Skeleton */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-gray-200 p-3 rounded-lg w-14 h-14"></div>
                <div>
                  <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
              <div className="h-10 bg-gray-200 rounded w-32"></div>
            </div>
          </div>

          {/* Post Skeletons */}
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-aalaw-red/10 p-3 rounded-lg">
                <svg className="w-8 h-8 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-aalaw-charcoal">Welcome to AA Law</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {posts.length} announcement{posts.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {isStaff && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 bg-aalaw-red hover:bg-aalaw-red-dark text-white"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Post
              </Button>
            )}
          </div>
        </div>

        {/* Posts List */}
        {posts.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="bg-gray-100 rounded-full p-6 mb-4">
                <svg
                  className="w-16 h-16 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No announcements yet</h3>
              <p className="text-sm text-gray-500 text-center max-w-sm">
                {isStaff
                  ? 'Create your first post to share important information with your team or clients.'
                  : 'There are no announcements for you at this time. Check back later for updates.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map((post: BulletinPost) => {
              const creatorName = `${post.creator?.firstName || ''} ${post.creator?.lastName || ''}`.trim() || 'AA Law';
              const contentPreview = truncateContent(post.content);
              const priorityBadge = getPriorityBadge(post.priority);
              
              // Count total attachments
              const imageAttachments = post.attachments?.filter(a => a.isImage) || [];
              const documentAttachments = post.attachments?.filter(a => !a.isImage) || [];
              const legacyImages = post.images || [];
              const totalImages = imageAttachments.length + legacyImages.length;
              const totalAttachments = totalImages + documentAttachments.length;
              
              return (
                <Card
                  key={post.id}
                  className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-aalaw-red group"
                >
                  <CardContent className="p-6">
                    {/* Card Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        {post.isPinned && (
                          <span className="text-aalaw-red text-lg" title="Pinned post">
                            📌
                          </span>
                        )}
                        {priorityBadge && (
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityBadge.color}`}
                          >
                            <span>{priorityBadge.icon}</span>
                            {priorityBadge.label}
                          </span>
                        )}
                        {totalAttachments > 0 && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            📎 {totalAttachments}
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">{formatTimestamp(post.createdAt)}</span>
                    </div>

                    {/* Content */}
                    <div
                      className="text-gray-700 whitespace-pre-wrap leading-relaxed mb-4 cursor-pointer"
                      onClick={() => setSelectedPost(post)}
                    >
                      {contentPreview}
                    </div>

                    {/* Attachment Thumbnails */}
                    {totalImages > 0 && (
                      <div className="flex gap-2 mb-4 overflow-x-auto">
                        {/* Image attachments */}
                        {imageAttachments.slice(0, 3).map((attachment) => (
                          <img
                            key={attachment.id}
                            src={attachment.url}
                            alt={attachment.fileName}
                            className="h-20 w-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setSelectedPost(post)}
                          />
                        ))}
                        {/* Legacy images */}
                        {imageAttachments.length < 3 && legacyImages.slice(0, 3 - imageAttachments.length).map((image) => (
                          <img
                            key={image.id}
                            src={image.thumbnailUrl || image.url}
                            alt="Post attachment"
                            className="h-20 w-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setSelectedPost(post)}
                          />
                        ))}
                        {totalImages > 3 && (
                          <div
                            className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                            onClick={() => setSelectedPost(post)}
                          >
                            <span className="text-sm text-gray-600 font-medium">
                              +{totalImages - 3}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Document Attachments Preview */}
                    {documentAttachments.length > 0 && (
                      <div className="mb-4 p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span>📄</span>
                          <span>
                            {documentAttachments.length} document{documentAttachments.length !== 1 ? 's' : ''} attached
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Reaction Bar */}
                    <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100">
                      {['👍', '❤️', '🎉', '👀'].map((emoji) => {
                        const count = getReactionCount(post.reactions, emoji);
                        const hasReacted = hasUserReacted(post.reactions, emoji, user?.id || '');
                        
                        return (
                          <button
                            key={emoji}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReaction(post.id, emoji, hasReacted);
                            }}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-sm transition-all ${
                              hasReacted
                                ? 'bg-aalaw-red/10 border-2 border-aalaw-red text-aalaw-red'
                                : 'bg-gray-100 border-2 border-transparent hover:bg-gray-200'
                            }`}
                          >
                            <span>{emoji}</span>
                            {count > 0 && <span className="text-xs font-medium">{count}</span>}
                          </button>
                        );
                      })}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      {contentPreview.endsWith('...') && (
                        <button
                          onClick={() => setSelectedPost(post)}
                          className="text-aalaw-red hover:text-aalaw-red-dark text-sm font-medium transition-colors"
                        >
                          Read more →
                        </button>
                      )}
                      <span className="text-xs text-gray-400 italic ml-auto">Posted by {creatorName}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create Post Modal */}
      {showCreateModal && (
        <CreatePostModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onPostCreated={() => {
            refetch();
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Post Detail Modal */}
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          isOpen={!!selectedPost}
          onClose={() => setSelectedPost(null)}
          onPostUpdated={() => {
            refetch();
            setSelectedPost(null);
          }}
          onPostDeleted={() => {
            refetch();
            setSelectedPost(null);
          }}
          currentUserId={user?.id}
          currentUserRole={user?.role}
          onReactionUpdate={() => refetch()}
        />
      )}
    </div>
  );
}
